import { useState, useRef, useEffect } from 'react';
import {
  MDXEditor,
  ListsToggle,
  MDXEditorMethods,
  BoldItalicUnderlineToggles,
  toolbarPlugin,
  CreateLink,
  linkPlugin,
  markdownShortcutPlugin,
  linkDialogPlugin,
  listsPlugin,
  headingsPlugin,
} from '@mdxeditor/editor';
import { IconSendMessage, ClickOutsideDetector, IconClose } from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import { useReadMessagesMutation } from '../hoc/store/services';
import { Channel, Message } from '../hoc/types';
import { setMessages } from '../hoc/store/slices/message';
import {
  setMainChannels,
  setRequestChannels,
} from '../hoc/store/slices/general';
import ChatMessages from './chat-messages';
import sendMessageHelper from '../hoc/helpers/send-message';
import { InProgressMessageTypes } from './types';

import '@mdxeditor/editor/style.css';
import cn from './messages.module.css';

const InProgressMessage = ({
  messages,
  setTopUpFilesSidepanelIsOpen,
  messageId,
  scrollWrapperRef,
  getNextMessages,
  pagination,
  messagesIsFetching,
  scrollRef,
  prevMessageIsFetching,
  setCurrentScrollTop,
  onAddFile,
  accounts,
  sendMessage,
  type,
  chatCreatedDate,
}: InProgressMessageTypes) => {
  const [fileMenuVisible, setFileMenuVisible] = useState(false);
  const [value, setValue] = useState('');
  const [maxLengthErrorVisible, setMaxLengthErrorVisible] = useState(false);
  const [toolbarVisible, setToolbarVisible] = useState(false);
  const [toolbarPositions, setToolbarPosition] = useState<{
    left: number;
    top: number;
  }>({ left: 0, top: 0 });

  const [readMessages] = useReadMessagesMutation();
  const [messageIsEdit, setMessageIsEdit] = useState(false);
  const [selectedForEditMessage, setSelectedForEditMessage] = useState<
    Message | undefined
  >(undefined);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const markdownRef = useRef<MDXEditorMethods | null>(null);
  const editMessageMarkdown = useRef<MDXEditorMethods | null>(null);

  const dispatch = useAppDispatch();

  const { mainChannels, requestChannels } = useAppSelector(
    (state) => state.messages.general,
  );

  const onSubmit = (isEdit?: boolean) => {
    const markdawnValue = markdownRef?.current?.getMarkdown();

    sendMessageHelper({
      messageId: messageId as string,
      value: markdawnValue?.replaceAll('\n\n\n\n', '\n\n&#x20;\n\n'),
      setMainChannels: (channels: Channel[]) =>
        dispatch(setMainChannels(channels)),
      setRequestChannels: (channels: Channel[]) =>
        dispatch(setRequestChannels(channels)),
      type,
      mainChannels,
      requestChannels,
    });

    if (!isEdit) {
      sendMessage(
        JSON.stringify({
          event: 'message',
          data: {
            channelId: messageId,
            content: markdawnValue?.replaceAll('\n\n\n\n', '\n\n&#x20;\n\n'),
          },
        }),
      );
    } else {
      sendMessage(
        JSON.stringify({
          event: 'edit-message',
          data: {
            channelId: messageId,
            content: markdawnValue?.replaceAll('\n\n\n\n', '\n\n&#x20;\n\n'),
            id: selectedForEditMessage?.id,
          },
        }),
      );

      setMessageIsEdit(false);
      setSelectedForEditMessage(undefined);
      setValue('');

      editMessageMarkdown?.current?.setMarkdown('');
      markdownRef?.current?.setMarkdown('');
    }

    const safariAgent = navigator.userAgent.indexOf('Safari') > -1;
    const chromeAgent = navigator.userAgent.indexOf('Chrome') > -1;

    let time = 5;

    if (!(safariAgent && chromeAgent)) {
      time = 40;
    }

    setTimeout(() => {
      if (scrollRef && scrollRef.current) {
        const { scrollHeight } = scrollRef.current;

        if (scrollHeight) {
          // eslint-disable-next-line no-param-reassign
          scrollRef.current.scrollTop = scrollHeight;
        }
      }
    }, time);

    setValue('');

    if (markdownRef?.current) {
      markdownRef?.current?.setMarkdown('');
    }
  };

  const onKeyUp = (e: any) => {
    if (e.key === 'Enter' && e.shiftKey) {
      e.preventDefault();

      if (value.length) {
        onSubmit(Boolean(selectedForEditMessage));
      }
    }
  };

  const onTextChange = (text: any) => {
    if (text.length <= 4096) {
      setValue(text);

      if (maxLengthErrorVisible) {
        setMaxLengthErrorVisible(false);
      }
    } else {
      setMaxLengthErrorVisible(true);
    }
  };

  useEffect(() => {
    if (
      messageId &&
      messages &&
      chatCreatedDate &&
      messages?.find((item) => !item.readAt)
    ) {
      if (
        messages?.filter(
          (item) => !item.id?.includes('message') && !item.readAt && item.id,
        ).length
      ) {
        readMessages({
          id: messageId,
          body: {
            // createdAtFrom: selectedChannel?.createdAt,
            // createdAtTo: new Date().toISOString(),
            messages: messages
              ?.filter((item) => !item.readAt)
              ?.map((item) => item.id) as string[],
            channelId: messageId,
          },
        });

        const nextMessages = messages.map((item) => ({
          ...item,
          readAt: new Date().toISOString(),
        }));

        dispatch(setMessages(nextMessages));
      }
    }
  }, [messageId, messages, chatCreatedDate]);

  return (
    <div className={cn.completedMessageContent}>
      <div className={cn.completedMessageChat} ref={scrollWrapperRef}>
        <ChatMessages
          messages={messages}
          scrollWrapperRef={scrollWrapperRef}
          messagesIsFetching={messagesIsFetching}
          scrollRef={scrollRef}
          getNextMessages={getNextMessages}
          pagination={pagination}
          prevMessageIsFetching={prevMessageIsFetching}
          setCurrentScrollTop={setCurrentScrollTop}
          messageId={messageId}
          setMessageIsEdit={setMessageIsEdit}
          setSelectedForEditMessage={setSelectedForEditMessage}
          selectedForEditMessage={selectedForEditMessage}
          editMessageMarkdown={editMessageMarkdown}
          markdownRef={markdownRef}
        />
      </div>
      <div
        className={`${cn.messageBottom} ${
          maxLengthErrorVisible ? cn.messageBottomError : ''
        }`}
      >
        <div className={`${cn.buttonWrapper} ${cn.addFileButtonWrapper}`}>
          {!messageIsEdit && (
            <ClickOutsideDetector setOptionsListVisible={setFileMenuVisible}>
              <>
                {fileMenuVisible && (
                  <div className={cn.fileMenu}>
                    <label htmlFor="add-file" className={cn.addFileLabel}>
                      <input
                        ref={inputRef}
                        type="file"
                        id="add-file"
                        className={cn.addFileInput}
                        onChange={(e) => {
                          onAddFile(e.target.files as FileList);
                          setFileMenuVisible(false);

                          if (inputRef.current) {
                            inputRef.current.value = '';
                          }
                        }}
                      />
                      Add file
                    </label>
                    {accounts && accounts.length > 0 && (
                      <button
                        type="button"
                        className={cn.fileMenuButton}
                        onClick={() => {
                          setTopUpFilesSidepanelIsOpen(true);
                          setFileMenuVisible(false);
                        }}
                      >
                        Send Top-up Options
                      </button>
                    )}
                  </div>
                )}
                <button
                  type="button"
                  className={cn.fileButton}
                  onClick={() => setFileMenuVisible(true)}
                  aria-label="Menu button"
                />
              </>
            </ClickOutsideDetector>
          )}
          {messageIsEdit && (
            <button
              type="button"
              className={cn.closeEditButton}
              onClick={() => {
                setMessageIsEdit(false);
                setSelectedForEditMessage(undefined);
                setValue('');
                editMessageMarkdown?.current?.setMarkdown('');
                markdownRef?.current?.setMarkdown('');
              }}
            >
              <IconClose width={16} height={16} />
            </button>
          )}
        </div>
        <div className={cn.messageBlockWrapper}>
          {messageIsEdit && (
            <div className={cn.editMessageText}>
              <span>Edit: </span>
              <MDXEditor
                ref={editMessageMarkdown}
                className={cn.editMessageItemText}
                markdown={selectedForEditMessage?.content || ''}
                readOnly
                plugins={[linkPlugin(), listsPlugin(), headingsPlugin()]}
              />
            </div>
          )}

          <ClickOutsideDetector
            className={cn.markdownWrapper}
            setOptionsListVisible={setToolbarVisible}
            onKeyUp={(e) => onKeyUp(e)}
            onMouseUp={(e) => {
              if (e.button === 2) {
                setToolbarPosition({
                  left: e.clientX,
                  top: e.clientY - 60,
                });
                setToolbarVisible(true);
              }

              if (e.button === 0 && toolbarVisible) {
                setToolbarVisible(false);
              }
            }}
            onContextMenu={(e) => e.preventDefault()}
          >
            <MDXEditor
              ref={markdownRef}
              className={cn.markdown}
              markdown={value || ''}
              placeholder="Message..."
              onChange={(e) => {
                onTextChange(e);
                setToolbarVisible(false);
              }}
              plugins={[
                headingsPlugin(),
                linkPlugin(),
                linkDialogPlugin(),
                listsPlugin(),
                markdownShortcutPlugin(),
                toolbarPlugin({
                  toolbarClassName: `${cn.toolbar} ${
                    toolbarVisible ? cn.active : ''
                  }`,
                  // eslint-disable-next-line react/no-unstable-nested-components
                  toolbarContents: () => (
                    <div
                      className={cn.toolbarWrapper}
                      style={{
                        left: toolbarPositions.left,
                        top: toolbarPositions.top,
                      }}
                    >
                      <ListsToggle />
                      <BoldItalicUnderlineToggles />
                      <CreateLink />
                    </div>
                  ),
                }),
              ]}
            />
          </ClickOutsideDetector>
        </div>

        <div className={cn.buttonWrapper}>
          <button
            type="button"
            className={cn.sendButton}
            onClick={() => {
              if (value.length) {
                onSubmit(Boolean(messageIsEdit));
              }
            }}
          >
            {messageIsEdit ? 'Save' : 'Send'}
            <div className={cn.iconWrapper}>
              <IconSendMessage />
            </div>
          </button>
        </div>
      </div>

      {maxLengthErrorVisible && (
        <div className={cn.maxLengthErrorText}>
          Max message length 4096 symbols
        </div>
      )}
    </div>
  );
};

export default InProgressMessage;
